import {
	CalendarIcon,
	CheckCircledIcon,
	IdCardIcon,
	ImageIcon,
	LockClosedIcon,
	MagicWandIcon,
	PlusIcon,
	ReaderIcon,
} from '@radix-ui/react-icons'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Form, Link, useNavigation } from 'react-router'
import {
	TICKET_TYPE_LABEL,
	TICKET_TYPES,
	TYPE_PRICE_FIELD,
} from '#app/domains/pricing/pure'
import {
	festivalClockAfter,
	festivalParts,
	formatClock,
	formatDayMonth,
	formatWeekday,
} from '#app/domains/program/pure'
import { type EventWithSeatsAndTickets } from '#app/utils/providers/events.server'
import { AddPerformerDialog } from '../performer/AddPerformerDialog.tsx'
import { EventCard } from './EventCard.tsx'
import { EventDescription } from './EventDescription.tsx'
import {
	countChanges,
	SuggestionReview,
	WordDiff,
} from './SuggestionReview.tsx'
import { ImageField } from '../../molecules/form/ImageField.tsx'

export type EventFormActionData = {
	errors?: {
		title?: string
		date?: string
		endTime?: string
		dropIn?: string
		totalSeats?: string
		prices?: string
		server?: string
	}
	event?: {
		id: string
		title: string
		date: string
	}
}

interface EventFormProps {
	event: EventWithSeatsAndTickets
	onInputChange: (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => void
	actionData?: EventFormActionData
	isEditing?: boolean
	allPerformers?: Array<{ id: string; name: string }>
	selectedPerformerIds?: string[]
	/** May add new performers from the form (the "Ny artist" dialog) */
	canAddPerformer?: boolean
	/** The current festival's pricing model; PER_CONCERT shows price inputs. */
	pricingModel?: string | null
	/**
	 * May set seats, prices and drop-in (admins). A performer linked to the
	 * concert sees them, but they are the festival's to change.
	 */
	canEditTickets?: boolean
	/**
	 * The performers a linked (non-admin) user is linked to: their way into
	 * this form, so they stay ticked and can't be unticked.
	 */
	ownPerformerIds?: string[]
}

const SHORT_DESCRIPTION_MAX = 300

/** "Arnested", "Arnested og Vorrå", "A, B og C" */
const joinNames = (names: string[]) =>
	names.length > 1
		? `${names.slice(0, -1).join(', ')} og ${names.at(-1)}`
		: (names[0] ?? '')

/** "fredag 27. november 2026 kl. 19:00", as the clock on Vorrå shows it */
const formatWhen = (date: Date | string) => {
	const parsed = new Date(date)
	if (isNaN(parsed.getTime())) return null
	return `${formatWeekday(parsed)} ${formatDayMonth(parsed)} ${festivalParts(parsed).year} kl. ${formatClock(parsed)}`
}

const KI_FAILED = 'Noko gjekk gale med KI-tenesta. Prøv igjen.'

/**
 * A KI resource's answer. Its own errors are nynorsk and shown as they are; a
 * refusal or a reply that isn't the resource's JSON (a log-in page, a crash)
 * gets a plain message instead of whatever the server said.
 */
async function readKiReply<T>(response: Response): Promise<T> {
	if (response.status === 401 || response.status === 403) {
		throw new Error(
			'Du har ikkje tilgang til KI-hjelp for dette arrangementet.',
		)
	}
	const body = (await response.json().catch(() => null)) as
		| (T & { error?: unknown })
		| null
	if (!body) throw new Error(KI_FAILED)
	if (!response.ok || body.error) {
		throw new Error(typeof body.error === 'string' ? body.error : KI_FAILED)
	}
	return body
}

/** The field ids whose errors the server can return, in page order. */
const ERROR_FIELDS = [
	['title', 'title'],
	['date', 'date'],
	['endTime', 'endTime'],
	['dropIn', 'dropIn'],
	['totalSeats', 'totalSeats'],
	['prices', TYPE_PRICE_FIELD[TICKET_TYPES[0]]],
] as const

/**
 * The concert editor, shared by "new" and "edit": four numbered sections
 * (each a fieldset with its legend) beside a live preview of the program card.
 */
export function EventForm({
	event,
	onInputChange,
	actionData,
	isEditing = false,
	allPerformers,
	selectedPerformerIds = [],
	canAddPerformer = false,
	pricingModel = null,
	canEditTickets = true,
	ownPerformerIds = [],
}: EventFormProps) {
	const isPerConcert = pricingModel === 'PER_CONCERT'
	const navigation = useNavigation()
	const isSaving =
		navigation.state === 'submitting' &&
		navigation.formData?.get('intent') !== 'delete'
	const [dateWithTimezone, setDateWithTimezone] = useState(
		event.date.toString(),
	)
	const [endTime, setEndTime] = useState(
		event.endsAt ? formatClock(event.endsAt) : '',
	)
	const [dropIn, setDropIn] = useState(Boolean(event.dropIn))
	const [shortDescription, setShortDescription] = useState(
		event.shortDescription || '',
	)
	const [generating, setGenerating] = useState(false)
	const [genError, setGenError] = useState<string | null>(null)
	/** a KI summary waiting to be approved or rejected */
	const [summarySuggestion, setSummarySuggestion] = useState<string | null>(
		null,
	)
	const [summaryNote, setSummaryNote] = useState<string | null>(null)
	const [proofreading, setProofreading] = useState(false)
	const [proofNote, setProofNote] = useState<string | null>(null)
	const [proofError, setProofError] = useState<string | null>(null)
	/** a KI correction waiting for a verdict; the description is locked meanwhile */
	const [proofSuggestion, setProofSuggestion] = useState<{
		before: string
		after: string
	} | null>(null)
	const shortDescriptionRef = useRef<HTMLTextAreaElement>(null)
	const titleRef = useRef<HTMLInputElement>(null)
	const descriptionRef = useRef<HTMLTextAreaElement>(null)
	const errors = actionData?.errors
	const [performers, setPerformers] = useState(allPerformers ?? [])
	const [selectedPerformers, setSelectedPerformers] =
		useState(selectedPerformerIds)
	const [addingPerformer, setAddingPerformer] = useState(false)
	const [performerNote, setPerformerNote] = useState('')
	const ownPerformerNames = performers
		.filter((p) => ownPerformerIds.includes(p.id))
		.map((p) => p.name)

	// send the keyboard to the first field the server refused
	useEffect(() => {
		const first = ERROR_FIELDS.find(([key]) => errors?.[key])
		if (first) document.getElementById(first[1])?.focus()
	}, [errors])

	async function generateSummary() {
		if (generating) return
		setGenerating(true)
		setGenError(null)
		setSummaryNote(null)
		try {
			const response = await fetch('/resources/event-summary', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					eventId: event.id || undefined,
					description:
						(descriptionRef.current?.value ?? event.description) || undefined,
					title: titleRef.current?.value ?? event.title,
				}),
			})
			const { summary } = await readKiReply<{ summary?: string }>(response)
			if (!summary) throw new Error(KI_FAILED)
			setSummarySuggestion(summary)
		} catch (error) {
			setGenError(
				error instanceof Error
					? error.message
					: 'Kunne ikkje generere samandrag.',
			)
		} finally {
			setGenerating(false)
		}
	}

	async function proofreadDescription() {
		const textarea = descriptionRef.current
		if (proofreading || !textarea) return
		const original = textarea.value
		setProofreading(true)
		setProofError(null)
		setProofNote(null)
		try {
			const response = await fetch('/resources/event-proofread', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					eventId: event.id || undefined,
					description: original,
				}),
			})
			const { text } = await readKiReply<{ text?: string }>(response)
			if (!text) throw new Error(KI_FAILED)
			// the editor kept typing while the AI worked: don't overwrite that
			if (textarea.value !== original) return
			if (text === original) {
				setProofNote('Fann ingenting å rette.')
				return
			}
			setProofSuggestion({ before: original, after: text })
		} catch (error) {
			setProofError(
				error instanceof Error ? error.message : 'Kunne ikkje rette teksten.',
			)
		} finally {
			setProofreading(false)
		}
	}

	function settleProofread(accept: boolean) {
		if (!proofSuggestion) return
		if (accept) setDescription(proofSuggestion.after)
		setProofSuggestion(null)
		setProofNote(accept ? 'Rettingane er tekne inn.' : 'Forslaget er forkasta.')
		descriptionRef.current?.focus()
	}

	function settleSummary(accept: boolean) {
		if (summarySuggestion === null) return
		if (accept) {
			setShortDescription(summarySuggestion)
			onInputChangePublic('shortDescription', summarySuggestion)
		}
		setSummarySuggestion(null)
		setSummaryNote(accept ? 'Forslaget er teke inn.' : 'Forslaget er forkasta.')
		shortDescriptionRef.current?.focus()
	}

	/** set the (uncontrolled) description and tell the preview */
	function setDescription(value: string) {
		if (descriptionRef.current) descriptionRef.current.value = value
		onInputChangePublic('description', value)
	}

	function onInputChangePublic(name: string, value: string) {
		// emit a synthetic event-compatible update for the preview
		onInputChange({
			target: { name, value },
		} as React.ChangeEvent<HTMLInputElement>)
	}

	const formatDateLocal = (dateString: string | undefined) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		if (isNaN(date.getTime())) return ''

		// Adjust to local timezone
		const localDate = new Date(
			date.getTime() - date.getTimezoneOffset() * 60000,
		)
		return localDate.toISOString().slice(0, 16) // Format as YYYY-MM-DDTHH:mm
	}

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const parsed = new Date(e.target.value)
		// A cleared/partial input is an invalid date; the browser blocks submit
		// via `required`, and the server re-validates.
		setDateWithTimezone(isNaN(parsed.getTime()) ? '' : parsed.toISOString())
		onInputChange(e)
		if (!isNaN(parsed.getTime())) previewEnd(parsed, endTime)
	}

	/** the preview's end follows the start, as the server will read it */
	function previewEnd(start: Date | string, clock: string) {
		const end = clock ? festivalClockAfter(start, clock) : null
		onInputChangePublic('endsAt', end ? end.toISOString() : '')
	}

	const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEndTime(e.target.value)
		previewEnd(dateWithTimezone || event.date, e.target.value)
	}

	const when = formatWhen(event.date)

	return (
		<div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
			<Form method="post" encType="multipart/form-data" className="grid gap-6">
				<FormSection
					step={1}
					icon={<ReaderIcon />}
					legend="Arrangementet"
					hint="Det publikum leser i programmet og på arrangementssiden."
				>
					<Field id="title" label="Tittel" error={errors?.title}>
						<input
							type="text"
							id="title"
							name="title"
							required
							autoComplete="off"
							className="input font-display w-full text-lg font-semibold"
							aria-invalid={Boolean(errors?.title)}
							aria-describedby={errors?.title ? 'title-error' : undefined}
							defaultValue={event.title}
							onChange={onInputChange}
							ref={titleRef}
						/>
					</Field>
					<div className="grid gap-1.5">
						<div className="flex flex-wrap items-end justify-between gap-2">
							<label htmlFor="description" className="text-sm font-semibold">
								Omtale
							</label>
							<button
								type="button"
								className="btn btn-ghost btn-xs border-line hover:border-base-content/30 gap-1.5 border font-semibold"
								onClick={() => void proofreadDescription()}
								disabled={proofreading || proofSuggestion !== null}
								title="Rettar skrivefeil etter nynorsknormen og ryddar Markdown-formateringa, utan å endre innhaldet"
							>
								<CheckCircledIcon aria-hidden="true" />
								{proofreading ? 'Rettar…' : 'Korrektur med KI'}
							</button>
						</div>
						<textarea
							id="description"
							name="description"
							rows={6}
							className="textarea field-sizing-content min-h-36 w-full text-base leading-relaxed read-only:opacity-60"
							aria-describedby="description-hint"
							readOnly={proofSuggestion !== null}
							defaultValue={event.description || ''}
							onChange={(e) => {
								setProofNote(null)
								onInputChange(e)
							}}
							ref={descriptionRef}
						/>
						<p id="description-hint" className="text-muted-ink text-xs">
							Blir vist på arrangementssida. Markdown fungerer: **feit**, lister
							og lenkjer.
						</p>
						{proofSuggestion ? (
							<SuggestionReview
								id="proofread-review"
								title={(() => {
									const n = countChanges(
										proofSuggestion.before,
										proofSuggestion.after,
									)
									return `Korrektur frå KI: ${n} ${n === 1 ? 'endring' : 'endringar'} — godta eller forkast før du skriv vidare`
								})()}
								acceptLabel="Godta rettingane"
								onAccept={() => settleProofread(true)}
								onReject={() => settleProofread(false)}
							>
								<WordDiff
									before={proofSuggestion.before}
									after={proofSuggestion.after}
								/>
							</SuggestionReview>
						) : null}
						<p aria-live="polite" className="text-sm empty:hidden">
							{proofError ? (
								<span className="text-error">{proofError}</span>
							) : (
								<span className="text-muted-ink">{proofNote}</span>
							)}
						</p>
					</div>
					<div className="grid gap-1.5">
						<div className="flex flex-wrap items-end justify-between gap-2">
							<label
								htmlFor="shortDescription"
								className="text-sm font-semibold"
							>
								Kort samandrag
							</label>
							<button
								type="button"
								className="btn btn-ghost btn-xs border-line hover:border-base-content/30 gap-1.5 border font-semibold"
								onClick={() => void generateSummary()}
								disabled={generating}
								title="Lagar eit kort samandrag med KI frå omtalen — du redigerer og lagrar sjølv"
							>
								<MagicWandIcon aria-hidden="true" />
								{generating
									? 'Genererer…'
									: summarySuggestion !== null
										? 'Nytt forslag'
										: 'Lag forslag med KI'}
							</button>
						</div>
						<textarea
							id="shortDescription"
							name="shortDescription"
							rows={2}
							className="textarea field-sizing-content min-h-20 w-full"
							maxLength={SHORT_DESCRIPTION_MAX}
							aria-describedby="shortDescription-hint"
							value={shortDescription}
							onChange={(e) => {
								setShortDescription(e.target.value)
								setSummaryNote(null)
								onInputChange(e)
							}}
							ref={shortDescriptionRef}
							placeholder="Nye og gamle viser med gitar og trekkspel i stova…"
						/>
						<div className="text-muted-ink flex justify-between gap-3 text-xs">
							<p id="shortDescription-hint">
								1–3 setningar til kortet, det trykte programmet og plakaten.
							</p>
							<span className="shrink-0 tabular-nums" aria-hidden="true">
								{shortDescription.length}/{SHORT_DESCRIPTION_MAX}
							</span>
						</div>
						{summarySuggestion !== null ? (
							<SuggestionReview
								id="summary-review"
								title={
									shortDescription
										? 'Forslag frå KI — erstattar samandraget over'
										: 'Forslag frå KI'
								}
								acceptLabel="Bruk forslaget"
								onAccept={() => settleSummary(true)}
								onReject={() => settleSummary(false)}
							>
								{summarySuggestion}
							</SuggestionReview>
						) : null}
						<p aria-live="polite" className="text-sm empty:hidden">
							{genError ? (
								<span className="text-error">{genError}</span>
							) : (
								<span className="text-muted-ink">{summaryNote}</span>
							)}
						</p>
					</div>
					{allPerformers && (performers.length > 0 || canAddPerformer) ? (
						<fieldset
							className="grid gap-2"
							aria-describedby={
								ownPerformerNames.length > 0
									? 'performers-hint own-performers-hint'
									: 'performers-hint'
							}
						>
							<legend className="float-left mb-2 w-full text-sm font-semibold">
								Kven opptrer?
							</legend>
							<div className="clear-both flex flex-wrap items-center gap-2">
								{performers.map((performer) => {
									const isOwn = ownPerformerIds.includes(performer.id)
									return (
										<label
											key={performer.id}
											className="border-line bg-base-100 has-checked:border-navy-2 has-checked:bg-navy-2 has-checked:text-paper has-focus-visible:outline-base-content flex min-h-9 cursor-pointer items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors has-disabled:cursor-default has-focus-visible:outline-2 has-focus-visible:outline-offset-2"
										>
											<input
												type="checkbox"
												name="performerIds"
												value={performer.id}
												className="checkbox checkbox-xs checkbox-accent not-checked:border-base-content/40"
												checked={
													isOwn || selectedPerformers.includes(performer.id)
												}
												disabled={isOwn}
												onChange={(e) => {
													const { checked } = e.target
													setSelectedPerformers((ids) =>
														checked
															? [...ids, performer.id]
															: ids.filter((id) => id !== performer.id),
													)
												}}
											/>
											{/* a disabled box isn't posted */}
											{isOwn ? (
												<input
													type="hidden"
													name="performerIds"
													value={performer.id}
												/>
											) : null}
											{performer.name}
											{isOwn ? (
												<LockClosedIcon
													className="size-3.5"
													aria-hidden="true"
												/>
											) : null}
										</label>
									)
								})}
								{performers.length === 0 ? (
									<span className="text-muted-ink text-sm">
										Ingen artistar enno.
									</span>
								) : null}
								{canAddPerformer ? (
									<button
										type="button"
										className="border-base-content/30 text-base-content/80 hover:border-base-content/60 hover:text-base-content flex min-h-9 items-center gap-1.5 rounded-full border border-dashed px-3 text-sm font-semibold transition-colors"
										onClick={() => setAddingPerformer(true)}
									>
										<PlusIcon aria-hidden="true" />
										Ny artist
									</button>
								) : null}
							</div>
							<p id="performers-hint" className="text-muted-ink text-xs">
								Arrangementet får lenkjer til artistsidene til dei du vel.
							</p>
							{ownPerformerNames.length > 0 ? (
								<p id="own-performers-hint" className="text-muted-ink text-xs">
									Du kan ikkje ta bort {joinNames(ownPerformerNames)} sjølv, for
									då mistar du tilgangen til arrangementet. Ta kontakt med
									festivalen om du ikkje skal vere med.
								</p>
							) : null}
							<p
								aria-live="polite"
								className="text-success text-sm empty:hidden"
							>
								{performerNote}
							</p>
						</fieldset>
					) : null}
				</FormSection>

				<FormSection
					step={2}
					icon={<CalendarIcon />}
					legend="Tid og stad"
					hint="Når dørene opnar, og kvar arrangementet hamnar på kartet."
				>
					<div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_auto]">
						<Field
							id="date"
							label="Dato og klokkeslett"
							hint={when ?? undefined}
							error={errors?.date}
						>
							<input
								type="datetime-local"
								id="date"
								name="date"
								required
								className="input w-full tabular-nums sm:max-w-xs"
								aria-invalid={Boolean(errors?.date)}
								aria-describedby={
									errors?.date ? 'date-error' : when ? 'date-hint' : undefined
								}
								defaultValue={formatDateLocal(event.date.toString())}
								onChange={handleDateChange}
							/>
							<input
								type="hidden"
								id="dateWithTimezone"
								name="dateWithTimezone"
								value={dateWithTimezone}
							/>
						</Field>
						<Field
							id="endTime"
							label="Sluttid"
							hint="Valfritt, t.d. når utstillinga stengjer."
							error={errors?.endTime}
						>
							<input
								type="time"
								id="endTime"
								name="endTime"
								className="input w-full tabular-nums sm:w-36"
								aria-invalid={Boolean(errors?.endTime)}
								aria-describedby={
									errors?.endTime ? 'endTime-error' : 'endTime-hint'
								}
								value={endTime}
								onChange={handleEndTimeChange}
							/>
						</Field>
					</div>
					<div className="grid gap-5 sm:grid-cols-2">
						<Field
							id="host"
							label="Heime hjå"
							hint="Blir vist på kortet, t.d. «Familien Hansen»."
						>
							<input
								type="text"
								id="host"
								name="host"
								autoComplete="off"
								className="input w-full"
								aria-describedby="host-hint"
								defaultValue={event.host || ''}
								onChange={onInputChange}
							/>
						</Field>
						<Field
							id="location"
							label="Adresse"
							hint="Plasserer arrangementet på kartet, t.d. «Liarvegen 53»."
						>
							<input
								type="text"
								id="location"
								name="location"
								autoComplete="off"
								className="input w-full"
								aria-describedby="location-hint"
								defaultValue={event.location || ''}
								onChange={onInputChange}
							/>
						</Field>
					</div>
				</FormSection>

				<FormSection
					step={3}
					icon={<ImageIcon />}
					legend="Bilete"
					hint="Blir brukt på kortet, arrangementssida og plakaten. Merk ei trygg sone, så det viktigaste alltid synest."
				>
					<ImageField
						name="photo"
						initialImageUrl={event.imageUrl || null}
						label={null}
						crop={{ mode: 'free' }}
						safeZone={{
							initial: event.imageSafeZone,
							frames: [
								{ label: 'Kort', aspect: 1 },
								{ label: 'Konsertside', aspect: 4 / 3 },
								{ label: 'Plakat', aspect: 210 / 88 },
							],
						}}
						helpText="Biletet blir beskore og komprimert i nettlesaren før det blir lagra."
					/>
				</FormSection>

				<FormSection
					step={4}
					icon={<IdCardIcon />}
					legend="Billettar"
					hint={
						!canEditTickets
							? 'Plassar og prisar set festivalen. Ta kontakt med festivalen om noko bør endrast.'
							: dropIn
								? 'Folk treng ikkje billett, og kjem og går som dei vil.'
								: isPerConcert
									? 'Kor mange som får plass, og kva det kostar.'
									: 'Kor mange som får plass.'
					}
				>
					{canEditTickets ? (
						<div className="grid gap-1.5">
							<label className="flex cursor-pointer items-start gap-3">
								<input
									type="checkbox"
									id="dropIn"
									name="dropIn"
									className="checkbox checkbox-sm mt-0.5"
									aria-invalid={Boolean(errors?.dropIn)}
									aria-describedby={
										errors?.dropIn ? 'dropIn-error' : 'dropIn-hint'
									}
									checked={dropIn}
									onChange={(e) => {
										setDropIn(e.target.checked)
										onInputChangePublic(
											'dropIn',
											e.target.checked ? 'true' : '',
										)
									}}
								/>
								<span className="grid gap-0.5">
									<span className="text-sm font-semibold">
										Treng ikkje billett — berre møt opp
									</span>
									<span id="dropIn-hint" className="text-muted-ink text-xs">
										For arrangement folk kan kome og gå på, som ei utstilling.
										Arrangementet får ingen plassar eller prisar, og ingenting
										blir selt.
									</span>
								</span>
							</label>
							{errors?.dropIn && (
								<p id="dropIn-error" className="text-error text-sm">
									{errors.dropIn}
								</p>
							)}
						</div>
					) : (
						<TicketTerms event={event} isPerConcert={isPerConcert} />
					)}
					{!canEditTickets || dropIn ? null : (
						<>
							<Field
								id="totalSeats"
								label="Tal på plassar"
								hint="Med 0 plassar står konserten som utseld."
								error={errors?.totalSeats}
							>
								<div className="input w-40 tabular-nums">
									<input
										type="number"
										id="totalSeats"
										name="totalSeats"
										required
										min={0}
										step={1}
										inputMode="numeric"
										aria-invalid={Boolean(errors?.totalSeats)}
										aria-describedby={
											errors?.totalSeats
												? 'totalSeats-error'
												: 'totalSeats-hint'
										}
										defaultValue={event.seats?.total ?? ''}
										onChange={onInputChange}
									/>
									<span className="text-muted-ink text-sm" aria-hidden="true">
										plassar
									</span>
								</div>
							</Field>
							{isPerConcert && (
								<fieldset className="grid gap-3" aria-describedby="prices-hint">
									<legend className="float-left mb-1 w-full text-sm font-semibold">
										Prisar
									</legend>
									<p
										id="prices-hint"
										className="text-muted-ink clear-both text-xs leading-relaxed"
									>
										Pris etter evne: kjøparen vel sjølv mellom Generøs, Ordinær
										og Lavpris. La eit felt stå tomt viss typen ikkje blir seld
										— 0 betyr gratis.
									</p>
									<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
										{TICKET_TYPES.map((type) => {
											const field = TYPE_PRICE_FIELD[type]
											return (
												<div key={type} className="grid gap-1.5">
													<label
														htmlFor={field}
														className="text-sm font-medium"
													>
														{TICKET_TYPE_LABEL[type]}
													</label>
													<div className="input w-full tabular-nums">
														<input
															type="number"
															id={field}
															name={field}
															min={0}
															inputMode="numeric"
															aria-invalid={Boolean(errors?.prices)}
															aria-describedby={
																errors?.prices ? 'prices-error' : undefined
															}
															defaultValue={event[field] ?? ''}
															onChange={onInputChange}
														/>
														<span
															className="text-muted-ink text-sm"
															aria-hidden="true"
														>
															kr
														</span>
													</div>
												</div>
											)
										})}
									</div>
									{errors?.prices && (
										<p id="prices-error" className="text-error text-sm">
											{errors.prices}
										</p>
									)}
								</fieldset>
							)}
						</>
					)}
				</FormSection>

				<div className="flex flex-wrap items-center gap-3 pt-1">
					<button
						type="submit"
						className="btn btn-primary min-w-44"
						disabled={isSaving}
					>
						{isSaving
							? 'Lagrar…'
							: isEditing
								? 'Lagre endringar'
								: 'Opprett arrangement'}
					</button>
					<Link
						to={isEditing ? `/events/${event.id}` : '/#program'}
						className="btn btn-ghost"
					>
						Avbryt
					</Link>
				</div>
			</Form>

			{canAddPerformer ? (
				<AddPerformerDialog
					open={addingPerformer}
					onClose={() => setAddingPerformer(false)}
					onAdded={(performer, existed) => {
						setPerformers((list) =>
							list.some(({ id }) => id === performer.id)
								? list
								: [...list, performer].sort((a, b) =>
										a.name.localeCompare(b.name, 'nb'),
									),
						)
						setSelectedPerformers((ids) =>
							ids.includes(performer.id) ? ids : [...ids, performer.id],
						)
						setPerformerNote(
							existed
								? `«${performer.name}» fanst frå før, og er vald.`
								: `«${performer.name}» er lagd til og vald.`,
						)
					}}
				/>
			) : null}

			<aside
				aria-labelledby="preview-title"
				className="grid gap-4 lg:sticky lg:top-6 lg:max-h-[calc(100dvh-3rem)] lg:overflow-y-auto lg:overscroll-contain lg:p-1"
			>
				<header>
					<p className="text-ember-ink text-xs font-bold tracking-[0.14em] uppercase">
						Førehandsvising
					</p>
					<h2 id="preview-title" className="mt-1 text-xl font-semibold">
						Slik ser det ut i programmet
					</h2>
				</header>
				<EventCard event={event as EventWithSeatsAndTickets} />
				{event.description ? (
					<div className="border-line border-t border-dashed pt-4">
						<p className="text-muted-ink mb-3 text-xs font-semibold tracking-wider uppercase">
							På arrangementssida
						</p>
						<EventDescription event={event as EventWithSeatsAndTickets} />
					</div>
				) : null}
			</aside>
		</div>
	)
}

/**
 * One numbered part of the form: a fieldset whose legend carries the step,
 * an icon tile like the front page's ticket steps, and a one-line hint.
 */
function FormSection({
	step,
	icon,
	legend,
	hint,
	children,
}: {
	step: number
	icon: ReactNode
	legend: string
	hint: string
	children: ReactNode
}) {
	const hintId = `section-${step}-hint`
	return (
		<fieldset
			className="border-line bg-sheet rounded-[1.25rem] border p-5 shadow-[0_18px_40px_-34px_rgb(40_25_10/0.5)] sm:p-7"
			aria-describedby={hintId}
		>
			<legend className="float-left flex w-full items-center gap-3">
				<span
					className="bg-navy-2 text-lamp grid size-10 shrink-0 place-items-center rounded-xl [&>svg]:size-[1.15rem]"
					aria-hidden="true"
				>
					{icon}
				</span>
				<span className="font-display text-xl leading-tight font-semibold">
					<span
						className="text-ember-ink mr-2 text-sm font-bold"
						aria-hidden="true"
					>
						{step}
					</span>
					{legend}
				</span>
			</legend>
			<p
				id={hintId}
				className="text-muted-ink clear-both pt-1 pl-[3.25rem] text-sm text-pretty"
			>
				{hint}
			</p>
			<div className="mt-6 grid gap-5">{children}</div>
		</fieldset>
	)
}

/** Seats and prices as the festival set them, for an editor who can't. */
function TicketTerms({
	event,
	isPerConcert,
}: {
	event: EventWithSeatsAndTickets
	isPerConcert: boolean
}) {
	if (event.dropIn) {
		return (
			<p className="text-sm font-semibold">
				Treng ikkje billett — berre møt opp
			</p>
		)
	}
	const terms = [
		{ label: 'Plassar', value: event.seats?.total ?? null, unit: '' },
		...(isPerConcert
			? TICKET_TYPES.map((type) => ({
					label: TICKET_TYPE_LABEL[type],
					value: event[TYPE_PRICE_FIELD[type]],
					unit: ' kr',
				}))
			: []),
	]
	return (
		<dl className="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-5">
			{terms.map(({ label, value, unit }) => (
				<div key={label} className="grid gap-1">
					<dt className="text-muted-ink text-sm">{label}</dt>
					<dd className="font-semibold tabular-nums">
						{value == null
							? 'Ikkje sett'
							: unit && value === 0
								? 'Gratis'
								: `${value}${unit}`}
					</dd>
				</div>
			))}
		</dl>
	)
}

/** A labelled control with an optional hint and the server's error below it. */
function Field({
	id,
	label,
	hint,
	error,
	children,
}: {
	id: string
	label: string
	hint?: string
	error?: string
	children: ReactNode
}) {
	return (
		<div className="grid content-start gap-1.5">
			<label htmlFor={id} className="text-sm font-semibold">
				{label}
			</label>
			{children}
			{hint && !error ? (
				<p id={`${id}-hint`} className="text-muted-ink text-xs">
					{hint}
				</p>
			) : null}
			{error ? (
				<p id={`${id}-error`} className="text-error text-sm">
					{error}
				</p>
			) : null}
		</div>
	)
}
